import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext.tsx';
import { formatPrice } from '../../lib/utils.ts';
import { LoadingSpinner, EmptyState } from '../../components/ui/Shared.tsx';

export default function CartPage() {
  const { cart, loading, fetchCart, updateItem, removeItem, total } = useCart();
  const navigate = useNavigate();

  useEffect(() => { fetchCart(); }, [fetchCart]);

  if (loading && !cart) return <LoadingSpinner />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
      <h1 className="text-3xl font-bold text-dark mb-8">Shopping Cart</h1>

      {!cart || cart.items.length === 0 ? (
        <EmptyState
          icon="🛒"
          title="Your cart is empty"
          description="Browse our store to add beverages to your cart"
          action={
            <Link to="/store" className="btn-gradient px-6 py-3 rounded-xl inline-block">
              Browse Store
            </Link>
          }
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-5 shadow-sm flex items-center gap-4 hover:shadow-md transition-all duration-300">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-lime/30 to-green/30 flex items-center justify-center shrink-0">
                  <span className="text-2xl">🍋</span>
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-dark text-sm truncate">{item.productName}</h3>
                  <p className="text-xs text-gray-500">{formatPrice(item.pricePerUnit)}/unit</p>
                </div>

                {/* Quantity */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateItem(idx, Math.max(1, item.quantity - 1))}
                    className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-sm font-bold hover:bg-lime/20 transition-colors"
                  >
                    −
                  </button>
                  <span className="w-12 text-center text-sm font-medium">{item.quantity}</span>
                  <button
                    onClick={() => updateItem(idx, item.quantity + 1)}
                    className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-sm font-bold hover:bg-lime/20 transition-colors"
                  >
                    +
                  </button>
                </div>

                {/* Subtotal */}
                <div className="text-right min-w-[80px]">
                  <p className="font-semibold text-dark text-sm">
                    {formatPrice(item.pricePerUnit * item.quantity)}
                  </p>
                </div>

                {/* Remove */}
                <button
                  onClick={() => removeItem(idx)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="glass rounded-2xl p-6 shadow-xl sticky top-24">
              <h3 className="font-bold text-dark text-lg mb-4">Order Summary</h3>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Items ({cart.items.length})</span>
                  <span className="font-medium text-dark">{formatPrice(total)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Shipping</span>
                  <span className="text-green font-medium">Calculated at checkout</span>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 mb-6">
                <div className="flex justify-between">
                  <span className="font-semibold text-dark">Subtotal</span>
                  <span className="text-xl font-bold text-dark">{formatPrice(total)}</span>
                </div>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="w-full btn-gradient py-3 rounded-xl font-semibold active:scale-[0.98] transition-transform"
              >
                Proceed to Checkout
              </button>

              <Link
                to="/store"
                className="block text-center mt-3 text-sm text-gray-500 hover:text-green transition-colors"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
